/**
 * AdminChurchAudit — Church Workspace Platform-First integration report.
 *
 * Route: /admin/audit/church
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Church, RefreshCw, CheckCircle2, AlertTriangle, Layers } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const PLATFORM_SERVICES = ['unified_ledger', 'workflow_engine', 'document_service', 'system_notifications', 'identity_registry'];

export default function AdminChurchAudit() {
  const q = useQuery({
    queryKey: ['church-audit'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/audit/church-workspace`, { headers: authH() });
      return r.data;
    },
  });

  const data = q.data || {};
  const summary = data.summary || {};
  const live = data.live_snapshot || {};
  const files = data.files || [];
  const files_using = summary.files_using_platform || {};

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-church-audit-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <Church className="h-7 w-7 text-violet-400" />
              Church Workspace Audit
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Platform-First integration report · which Church routes consume the shared platform
              services (Unified Ledger · Workflow Engine · Document Service · Notification Service ·
              Identity Registry) vs. still doing things inline.
            </p>
          </div>
          <button
            data-testid="church-audit-refresh"
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm hover:bg-slate-800"
            onClick={() => q.refetch()}
          >
            <RefreshCw className="mr-2 inline h-4 w-4" /> Refresh
          </button>
        </div>

        {/* Live snapshot */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4 text-violet-400" /> Live snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div data-testid="live-churches">
                <div className="text-xs uppercase text-slate-500">Churches</div>
                <div className="text-2xl font-semibold">{live.churches_total ?? 0}</div>
              </div>
              <div data-testid="live-members">
                <div className="text-xs uppercase text-slate-500">Members</div>
                <div className="text-2xl font-semibold">{live.church_members_total ?? 0}</div>
              </div>
              <div data-testid="live-ledger-30d">
                <div className="text-xs uppercase text-slate-500">Ledger tx (30d)</div>
                <div className="text-2xl font-semibold">{live.ledger_church_transactions_30d ?? 0}</div>
                <div className="text-xs text-slate-500">Total: {live.ledger_church_transactions_total ?? 0}</div>
              </div>
              <div data-testid="live-workflow">
                <div className="text-xs uppercase text-slate-500">Workflow events (30d)</div>
                <div className="text-2xl font-semibold">{live.workflow_events_church_30d ?? 0}</div>
              </div>
              <div data-testid="live-notifications">
                <div className="text-xs uppercase text-slate-500">Notifications (30d)</div>
                <div className="text-2xl font-semibold">{live.notifications_church_30d ?? 0}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">Files scanned</div>
                <div className="text-2xl font-semibold">{summary.files_scanned ?? 0}</div>
                <div className="text-xs text-slate-500">{summary.total_endpoints ?? 0} endpoints · {summary.total_lines ?? 0} lines</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform service adoption */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Platform service adoption (files consuming each service)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
              {PLATFORM_SERVICES.map((svc) => {
                const count = files_using[svc] || 0;
                const total = summary.files_scanned || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={svc} data-testid={`svc-${svc}`} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <div className="text-xs text-slate-400">{svc.replace(/_/g, ' ')}</div>
                    <div className="mt-1 text-xl font-semibold">{count}<span className="ml-1 text-xs font-normal text-slate-500">/{total}</span></div>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-slate-800">
                      <div className="h-full rounded-full bg-violet-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" /> Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm" data-testid="recommendations-list">
              {(data.recommendations || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 rounded-md border border-slate-800 bg-slate-950/60 p-3">
                  {r.startsWith('Church workspace is fully') ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
                  )}
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Files */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Church files (lowest platform score first)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="church-files-table">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-4">File</th>
                  <th className="py-2 pr-4 text-right">Lines</th>
                  <th className="py-2 pr-4 text-right">Endpoints</th>
                  <th className="py-2 pr-4">Consumes</th>
                  <th className="py-2 pr-4">Direct</th>
                  <th className="py-2 pr-4 text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.file} className="border-b border-slate-800/60" data-testid="church-file-row">
                    <td className="py-2 pr-4 font-mono text-slate-300">{f.path}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{f.lines}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{f.endpoints}</td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(f.consumes || {}).map((k) => (
                          <Badge key={k} variant="outline" className="border-emerald-500/40 text-emerald-300 text-xs">
                            {k}
                          </Badge>
                        ))}
                        {Object.keys(f.consumes || {}).length === 0 && <span className="text-xs text-slate-600">—</span>}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(f.direct_services || {}).map((k) => (
                          <Badge key={k} variant="outline" className="border-rose-500/40 text-rose-300 text-xs">
                            {k}
                          </Badge>
                        ))}
                        {Object.keys(f.direct_services || {}).length === 0 && (
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-right font-semibold">
                      <span className={f.platform_score >= 3 ? 'text-emerald-300' : (f.platform_score >= 1 ? 'text-amber-300' : 'text-rose-300')}>
                        {f.platform_score}/5
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
